// @i18n-ignore-file
import { parse, serialize } from 'cookie'

import { LOCALE_COOKIE, DEFAULT_LANGUAGE } from '@src/config/i18n.config'
import { shouldEnableI18nClient } from '@src/server-side/utils/i18n-preview.utils'

import isBrowser from '../../src/quince-site-common/src/utils/isBrowser'
import getCookie from '../quince-site-common/src/utils/getCookie'

export interface CookieOptions {
  isGlobal?: boolean
  expires?: number | Date
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: 'strict' | 'lax' | 'none'
}

/**
 * Simple enhanced cookie class that adds locale support when needed
 *
 *
 * Key Format:
 * - Global cookies (isGlobal=true): originalKey (unchanged for backward compatibility)
 * - Localized cookies (isGlobal=false): {locale}:originalKey (e.g., en-us:userPrefs) - DEFAULT
 */
class EnhancedCookie {
  private isI18nEnabled: boolean

  constructor(isI18nEnabled: boolean) {
    this.isI18nEnabled = isI18nEnabled
  }

  private getLocalizedKey(key: string, options: CookieOptions = {}): string {
    const { isGlobal = true } = options

    // If global or i18n disabled, use original key (backward compatible)
    if (isGlobal || !this.isI18nEnabled) {
      return key
    }

    // For localized data, prefix with current locale
    const locale = getCookie(LOCALE_COOKIE) ?? DEFAULT_LANGUAGE
    return `${locale}:${key}`
  }

  private getCookieValue(name: string): string | undefined {
    if (!isBrowser()) return undefined

    const parsedCookie = parse(document.cookie)
    return parsedCookie[name]
  }

  private setCookieValue(name: string, value: string, options: Omit<CookieOptions, 'isGlobal'>): void {
    if (!isBrowser()) return

    const expires =
      options.expires instanceof Date
        ? options.expires
        : new Date(Date.now() + (options.expires ?? 400 * 24 * 60) * 60 * 1000)

    const cookieString = serialize(name, value, {
      path: options.path ?? '/',
      expires,
      secure: options.secure,
      sameSite: options.sameSite ?? 'lax',
      domain: options.domain ?? undefined,
    })

    document.cookie = cookieString
  }

  private removeCookieValue(name: string, options: Omit<CookieOptions, 'isGlobal'>): void {
    if (!isBrowser()) return

    const cookieOptions = {
      ...options,
      expires: new Date(0), // Set expiry to past date to remove cookie
    }
    this.setCookieValue(name, '', cookieOptions)
  }

  /**
   * Gets a cookie value with optional locale support
   * @param key - The key to retrieve
   * @param options - Cookie options (optional)
   * @param options.isGlobal - true: use original key, false: use locale:key format (default: false)
   * @returns The stored value or null if not found
   *
   *
   * @example
   * // Localized cookie (default behavior)
   * const preferences = cookies.getCookieLocalized('userPrefs')
   * // Looks for: "en-us:userPrefs"
   *
   *
   * // Global cookie (explicit)
   * const authToken = cookies.getCookieLocalized('authToken', { isGlobal: true })
   * // Looks for: "authToken"
   */
  getCookieLocalized(key: string, options: CookieOptions = {}): string | null {
    if (!isBrowser()) return null

    try {
      const cookieKey = this.getLocalizedKey(key, options)
      const value = this.getCookieValue(cookieKey)
      return value ?? null
    } catch (error) {
      console.error('Error getting cookie:', error)
      return null
    }
  }

  /**
   * Sets a cookie with optional locale support
   * @param key - The key to store
   * @param value - The value to store
   * @param options - Cookie options (optional)
   * @param options.isGlobal - true: use original key, false: use locale:key format (default: false)
   *
   *
   * @example
   * // Localized cookie (default behavior)
   * cookies.setCookieLocalized('userPrefs', preferences)
   * // Stores as: "en-us:userPrefs" and removes old "userPrefs" if it exists
   *
   *
   * // Global cookie (explicit)
   * cookies.setCookieLocalized('authToken', token, { isGlobal: true })
   * // Stores as: "authToken"
   */
  setCookieLocalized(key: string, value: string, options: CookieOptions = {}): void {
    if (!isBrowser()) return

    try {
      const { isGlobal = false, ...cookieOptions } = options
      const cookieKey = this.getLocalizedKey(key, options)

      this.setCookieValue(cookieKey, value, cookieOptions)

      // Clean up old non-localized version when setting localized data
      if (!isGlobal && this.isI18nEnabled && cookieKey !== key) {
        this.removeCookieValue(key, cookieOptions)
      }
    } catch (error) {
      console.error('Error setting cookie:', error)
    }
  }

  /**
   * Removes a cookie
   * @param key - The key to remove
   * @param options - Cookie options (optional)
   */
  removeCookieLocalized(key: string, options: CookieOptions = {}): void {
    if (!isBrowser()) return

    try {
      const cookieKey = this.getLocalizedKey(key, options)
      this.removeCookieValue(cookieKey, options)
    } catch (error) {
      console.error('Error removing cookie:', error)
    }
  }

  /**
   * Checks if a cookie exists
   * @param key - The key to check
   * @param options - Cookie options (optional)
   * @returns true if the cookie exists, false otherwise
   */
  hasCookieLocalized(key: string, options: CookieOptions = {}): boolean {
    if (!isBrowser()) return false

    try {
      const cookieKey = this.getLocalizedKey(key, options)
      return !!this.getCookieValue(cookieKey)
    } catch (error) {
      console.error('Error checking cookie:', error)
      return false
    }
  }
}

// Create a singleton instance
let cookieInstance: EnhancedCookie | null = null

export const getCookieStorage = (): EnhancedCookie => {
  cookieInstance ??= new EnhancedCookie(shouldEnableI18nClient())
  return cookieInstance
}
